import { CheckIcon, SparklesIcon, XIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { InherentRiskCategoryEnum, InherentRiskItem } from '@/gen/inventory/v1/company_service_pb'

import { cn } from '@/lib/ui/style-helpers'

import { SET_IRQ_MODAL_LABEL } from '@/pages/company-drawer/company-overview/set-irq-modal/labels'
import { InsightSection } from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/insight-section'
import { SourceAvatarsIndicator } from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/source-avatars-indicator'
import { groupByAnswer } from '@/pages/company-drawer/company-overview/set-irq-modal/lema-suggestions-card/utils'

import { Button } from '@/components/lemmala-design-system/button/button'
import { Checkbox } from '@/components/lemmala-design-system/checkbox/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/lemmala-design-system/collapsible'

type LemaSuggestionsCardProps = {
  suggestions: InherentRiskItem[]
  selectedIds: string[]
  categoryEnum: InherentRiskCategoryEnum
  mode?: 'multi' | 'single'
  onToggle?: (optionId: string) => void
  onApplyAll?: () => void
  onRemoveAll?: () => void
  businessOwnerName?: string
}

export const LemaSuggestionsCard = ({
  suggestions,
  selectedIds,
  categoryEnum,
  mode = 'multi',
  onToggle,
  onApplyAll,
  onRemoveAll,
  businessOwnerName,
}: LemaSuggestionsCardProps) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())

  const groups = useMemo(() => groupByAnswer(suggestions), [suggestions])

  if (groups.length === 0) return null

  const allApplied = groups.every((g) => selectedIds.includes(g.id))

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className='overflow-hidden rounded-md border border-main-border-default bg-main-background-muted shadow-sm'>
      <div className='flex items-center gap-1.5 px-4 pt-4'>
        <SparklesIcon className='size-4 text-main-foreground-tertiary' />
        <span className='text-main-foreground-primary text-label-default'>
          {SET_IRQ_MODAL_LABEL.LEMA_AI_SUGGESTIONS}
        </span>
      </div>

      <div className={cn('flex flex-col gap-2 px-4 pt-4', mode === 'single' ? 'pb-4' : '')}>
        {groups.map((group) => {
          const isChecked = selectedIds.includes(group.id)
          const expanded = expandedIds.has(group.id)

          return (
            <Collapsible
              key={group.id}
              open={expanded}
              onOpenChange={() => toggleExpanded(group.id)}
              className={cn(
                'flex flex-col rounded border border-main-border-default bg-main-background-card',
                expanded && 'shadow-tooltip',
              )}
            >
              <CollapsibleTrigger className='flex w-full items-center justify-between gap-3 rounded px-2.5 py-2 text-left outline-none hover:bg-main-background-interactive-list-hover focus-visible:ring-2 focus-visible:ring-main-border-focus'>
                <div className='flex min-w-0 items-center gap-3'>
                  {mode === 'multi' && (
                    <span
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      className='flex'
                    >
                      <Checkbox
                        variant='ai'
                        checked={isChecked}
                        onCheckedChange={() => onToggle?.(group.id)}
                      />
                    </span>
                  )}
                  <span
                    className={cn(
                      'truncate text-main-foreground-primary',
                      expanded ? 'text-label-default' : 'text-body-default',
                    )}
                  >
                    {group.displayName}
                  </span>
                </div>
                <SourceAvatarsIndicator sources={group.sources} expanded={expanded} />
              </CollapsibleTrigger>
              <CollapsibleContent>
                <InsightSection
                  sources={group.sources}
                  answer={group.displayName}
                  categoryEnum={categoryEnum}
                  businessOwnerName={businessOwnerName}
                />
              </CollapsibleContent>
            </Collapsible>
          )
        })}
      </div>

      {mode === 'multi' && (
        <div className='flex items-center justify-end px-4 py-2'>
          {allApplied ? (
            <Button
              variant='link'
              size='sm'
              onClick={onRemoveAll}
              className='text-main-foreground-accent'
            >
              <XIcon className='size-3.5' />
              {SET_IRQ_MODAL_LABEL.REMOVE_ALL}
            </Button>
          ) : (
            <Button
              variant='link'
              size='sm'
              onClick={onApplyAll}
              className='text-main-foreground-accent'
            >
              <CheckIcon className='size-3.5' />
              {SET_IRQ_MODAL_LABEL.APPLY_ALL}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
